import { useEffect, useMemo, useState } from 'react';

import { breakpoints } from '../theme/theme';
import { isClient } from '../utils/isClient';

export type Matches = Record<
  'matchesMobile' | 'matchesTablet' | 'matchesDesktop' | 'matchesLargeDesktop',
  boolean
>;

export const queries = breakpoints.map(breakpoint => `(min-width: ${breakpoint})`);

export const useMatchesViewport = (
  defaultMatchesTablet = true,
  defaultMatchesDesktop = true,
  defaultMatchesLargeDesktop = true
) => {
  const [matchesTablet, setMatchesTablet] = useState(defaultMatchesTablet);
  const [matchesDesktop, setMatchesDesktop] = useState(defaultMatchesDesktop);
  const [matchesLargeDesktop, setMatchesLargeDesktop] = useState(defaultMatchesLargeDesktop);
  const [isMounted, setIsMounted] = useState(false);
  const mediaQueries = useMemo(
    () => (isClient() ? queries.map(query => window.matchMedia(query)) : []),
    queries
  );

  useEffect(() => {
    const matchesSetters = [setMatchesTablet, setMatchesDesktop, setMatchesLargeDesktop];
    mediaQueries.forEach(({ matches }, index) => matchesSetters[index](matches));
    const listenerHandlers = matchesSetters.map(
      setter =>
        ({ matches }: MediaQueryListEvent) =>
          setter(matches)
    );

    mediaQueries.forEach((mediaQuery, index) =>
      // since addEventListener is not supported by Safari < 14, use deprecated method as a fallback
      mediaQuery.addEventListener
        ? mediaQuery.addEventListener('change', listenerHandlers[index])
        : mediaQuery.addListener(listenerHandlers[index])
    );
    setIsMounted(true);

    return () => {
      mediaQueries.forEach((mediaQuery, index) =>
        mediaQuery.removeEventListener
          ? mediaQuery.removeEventListener('change', listenerHandlers[index])
          : mediaQuery.removeListener(listenerHandlers[index])
      );
      setIsMounted(false);
    };
  }, [mediaQueries]);

  return {
    matches: {
      matchesMobile: !matchesTablet && !matchesDesktop && !matchesLargeDesktop,
      matchesTablet,
      matchesDesktop,
      matchesLargeDesktop,
    },
    isMounted,
  };
};
